// render-pc-sheet.ts — pane router for the PC-sheet bundle. Maps a pane name to its
// TS renderer and paints it into the mount container from the caster blob. This is
// the seam every newly-ported tab plugs into: add a case, the mount scanner +
// surgical caster_update re-render pick it up for free.
//
// Returns true when a TS renderer handled the pane (caller marks it done); false
// only for unknown pane names. Unsupported emitted panes show a named refusal.

import { displayRefusal } from './k5-runtime';
import { creatureDisplayAccess } from '../janus-engine/display/creature-display-access';
import type { CachedCaster, PaneName } from './types';
import { buildDefensesHtml, hydrateDefenses } from './tabs/defenses';
import { buildPlayerNotesHtml } from './tabs/player-notes';
import { buildPlayerSettingsHtml } from './tabs/player-settings';
import { buildCharacterDescriptionHtml } from './tabs/character-description';
import { buildEquipmentSlotsHtml } from './tabs/equipment-slots';
import { renderAbilitySections, renderAbilitySection } from './tabs/ability-section';
import { renderWeaponAbilities, renderOrbQueueSection } from './tabs/weapon-abilities';
import { renderPassiveAbilities, hydratePassiveTree } from './tabs/passive-abilities';
import { renderEquippedWeapons, renderNaturalWeapons } from './tabs/weapon-tables';
import { renderCoreAttacks } from './tabs/core-attacks';
import { renderCastableSpells } from './tabs/castable-spells';
import { renderMentas } from './tabs/mentas';
import { renderMovementPane } from './tabs/movement';
import { renderInventory, renderMasterControl, renderEquipmentWindow, renderOnHandWindow, renderBag } from './tabs/inventory';
import { renderCrafting } from './tabs/crafting';
import { renderPrepareSpells } from './tabs/prepare-spells';
import { renderSummary } from './tabs/summary';
import { renderSkills } from './tabs/skills';
import { paintMarkup, paintInventoryMarkup } from './paint-markup';
import { renderActionBar } from './tabs/action-bar';

// Upgrade any freshly-painted `select.ts-select:not(.tomselected)` (the ability rows' damage-type
// chooser + summon/shapeshift selects) — same guarded call the spells/mentas panes make. Idempotent;
// no-op until the tom-select bundle has loaded.
function initTomSelects(): void {
    const init = window.initializeTomSelect;
    if (typeof init === 'function') { try { init(); } catch { /* noop */ } }
}

export function renderPcSheetPane(
    paneName: PaneName,
    caster: CachedCaster,
    casterId: string,
    container: HTMLElement,
): boolean {
    if (!container) return false;
    // Stamp the caster ROOT catalog (UUID→full-item) on the rune engine BEFORE any tab paints/hydrates
    // its rune medallions. name1() uses it to resolve the bare-UUID reference layers (charge abilities,
    // contest skills, …) to their item_name — the CDN icon files are name-keyed, so an unresolved UUID
    // 404s. Every pane below hydrates via window.ftMountRuneIcons (directly or inside a tab renderer),
    // and the footer scanner no longer overwrites a globally-set catalog when a blob carries none.
    try { window.RuneIcon?.setCatalog?.(caster.catalog ?? {}); } catch { /* engine not ready */ }
    // "Static item-creature" mode: a creature statblock viewed on its own item.php page (not placed in
    // an encounter). Its abilities/attacks are reference-only and its gear lives in inventory_equipped.
    // The PHP mount sets data-ft-static-creature="1"; live PC + encounter mounts omit it ⇒ isStatic=false.
    const isStatic = container.getAttribute('data-ft-static-creature') === '1';
    const card = container.closest<HTMLElement>('[data-ft-creature-card-cache="1"]');
    // Catalog cards mark the whole card static; only attack/ability panes carry
    // the per-pane flag because it also selects their reference-only renderer.
    if (card && card.dataset.ftStaticCreature !== '1' && !isStatic) {
        const access = creatureDisplayAccess(caster, card.dataset.ftViewerCasterId, card.dataset.ftViewerCanControl === '1');
        const privatePanes = ['skills', 'ability_sections', 'weapon_abilities', 'passive_abilities', 'castable_spells', 'mentas',
            'movement', 'defenses', 'creature_summary', 'creature_core_abilities', 'creature_gm', 'core_attacks', 'equipped_weapons', 'natural_weapons'];
        if ((!access.control && privatePanes.includes(paneName)) || (paneName === 'inventory' && !access.inventory)) {
            paintMarkup(container, '');
            return true;
        }
    }
    try {
        switch (paneName) {
            case 'pc_core_abilities': {
                const render = (window as unknown as { ftRenderPcCoreAbilitiesFromCache?: (id: string, target: HTMLElement, sideInfo: boolean) => boolean }).ftRenderPcCoreAbilitiesFromCache;
                const painted = container.firstChild;
                if (!render?.(casterId, container, container.dataset.ftSideInfo === '1')) container.innerHTML = displayRefusal('unported:creature-display:core-renderer-not-held');
                // The renderer keeps an unchanged block (paintMarkup); its icons are already mounted.
                else if (painted && container.firstChild === painted) return true;
                window.ftMountRuneIcons?.(container);
                window.ftMountIcons?.(container);
                return true;
            }
            case 'defenses':
                paintMarkup(container, buildDefensesHtml(caster, casterId));
                // Status buttons render from the cache into their own host, so they refresh even when the markup holds.
                hydrateDefenses(container, caster, casterId);
                return true;
            case 'player_notes':
                paintMarkup(container, buildPlayerNotesHtml(caster, casterId));
                return true;
            case 'player_settings':
                paintMarkup(container, buildPlayerSettingsHtml(caster, casterId));
                return true;
            case 'character_description':
                paintMarkup(container, buildCharacterDescriptionHtml(caster, casterId));
                return true;
            case 'equipment_slots':
                paintMarkup(container, buildEquipmentSlotsHtml(caster));
                return true;
            case 'ability_sections':
                if (!paintMarkup(container, renderAbilitySections(caster, casterId, isStatic))) return true;
                // Hydrate the rune-icon faces on the freshly-painted ability buttons, then the
                // <i class="ft-icon"> placeholders the rows emit (icon_ph twin) — the latter turns
                // the ability-type header chips into rune type-badges and renders source/recharge art.
                window.ftMountRuneIcons?.(container);
                window.ftMountIcons?.(container);
                initTomSelects();
                return true;
            case 'weapon_abilities':
                // Attack + opportunity-attack ability tables (port of the render_abilities()
                // calls in _weapons_equipped_inc.php). Same hydration as ability_sections.
                if (!paintMarkup(container, renderWeaponAbilities(caster, casterId, isStatic))) return true;
                window.ftMountRuneIcons?.(container);
                window.ftMountIcons?.(container);
                initTomSelects();
                return true;
            case 'orb_queue':
                // Machinist orb queue strip for the Abilities tab's class-mechanics section. A pane
                // (not PHP) because ORDER is the mechanic and it changes on every channel/evoke —
                // this repaints surgically on caster_update. iconPh emits <i class="ft-icon">
                // placeholders, so hydrate them like the other icon-bearing panes.
                if (!paintMarkup(container, renderOrbQueueSection(caster))) return true;
                window.ftMountIcons?.(container);
                return true;
            case 'passive_abilities':
                // Passive abilities tree (port of render_passive_abilities_table).
                if (!paintMarkup(container, renderPassiveAbilities(caster, casterId, isStatic))) return true;
                window.ftMountRuneIcons?.(container);
                window.ftMountIcons?.(container);
                // Collapse delegates (bound once) + search↔collapse state for the fresh paint.
                hydratePassiveTree(container);
                return true;
            case 'ability_section':
                // A SINGLE ability-type section (creature list-display branch, Phase D). The type
                // rides on data-ft-ability-type on the mount element.
                if (!paintMarkup(container, renderAbilitySection(
                    caster,
                    container.getAttribute('data-ft-ability-type') || 'ability',
                    isStatic,
                ))) return true;
                window.ftMountRuneIcons?.(container);
                window.ftMountIcons?.(container);
                initTomSelects();
                return true;
            case 'equipped_weapons':
                paintMarkup(container, renderEquippedWeapons(caster, casterId, isStatic));
                return true;
            case 'natural_weapons':
                // The unified creature card's TS shell (object-card render-creature-card.ts) mounts
                // this above equipped_weapons; '' for creatures without the layer keeps it inert.
                paintMarkup(container, renderNaturalWeapons(caster, casterId, isStatic));
                return true;
            case 'core_attacks':
                paintMarkup(container, renderCoreAttacks(caster, casterId));
                return true;
            case 'castable_spells':
                // The selector reads the held native lists and resolves their catalog definitions.
                void renderCastableSpells(caster, casterId, container);
                return true;
            case 'mentas':
                void renderMentas(caster, casterId, container);
                return true;
            case 'movement':
                renderMovementPane(caster, casterId, container);
                return true;
            case 'action_bar':
                // Docked BG3/WoW-style hotbars (above the tab strip, persists across tabs). Reads
                // caster.action_bars; reuses generateAbilityButton for filled slots. Repaints on
                // caster_update so +/- row changes + (later) drag-drop reflect live.
                renderActionBar(caster, casterId, container);
                return true;
            case 'skills':
                // Shared Skills tab (PC + encounter sheets). Emits icon_ph twins (skill / tier /
                // essence / adv-dis art) → hydrate via ftMountIcons. The nav-tab filters reuse the
                // existing global selectCharacterSkillsFilter (DOM-class toggling), no JS to wire here.
                if (!paintMarkup(container, renderSkills(caster, casterId, container))) return true;
                window.ftMountIcons?.(container);
                return true;
            case 'inventory':
                // EC loot/peek: the mount passes the viewer's caster-id so the pane can derive owner|gm|
                // loot|peek mode from the blob (PC sheets omit it → owner-only, peek never fires).
                if (!paintInventoryMarkup(container, renderInventory(
                    caster,
                    casterId,
                    container.getAttribute('data-ft-viewer-caster-id') || '',
                    container.closest('#div_caster_sheet[data-ft-player-card-cache="1"]') !== null,
                ))) return true;
                // Tiles are emitted inline (renderInventoryThumbnail), so no ft-inv-tile-mount
                // rescan is needed. Drag-drop is document-level delegation + search auto-wires;
                // re-scan both against the freshly-painted markup. Order: HTML in DOM → drag → search.
                window.refreshDragAndDrop?.();
                window.fateTideRefreshInventoryUnequippedSearch?.();
                return true;
            case 'master_control':
                // Fullscreen master inventory control: the strip of section toggles (Equipment / On Hand /
                // Bags). Cheap (just buttons), but mounted as a pane so it repaints on caster_update when the
                // equipped-bag set changes. No drag tiles here, but harmless to re-scan.
                paintMarkup(container, renderMasterControl(
                    caster,
                    casterId,
                    container.getAttribute('data-ft-viewer-caster-id') || '',
                ));
                return true;
            case 'equipment_window':
                // Fullscreen section window: the equipment paper-doll on its own. Same equip dropzone +
                // drag-wired slot tiles as the inventory tab → re-scan after paint.
                if (!paintInventoryMarkup(container, renderEquipmentWindow(
                    caster,
                    casterId,
                    container.getAttribute('data-ft-viewer-caster-id') || '',
                ))) return true;
                window.refreshDragAndDrop?.();
                return true;
            case 'onhand_window':
                // Fullscreen section window: the on-hand grid on its own (own weight/GP banner). Re-scan
                // wires drag-drop + the unequipped search like the inventory tab.
                if (!paintInventoryMarkup(container, renderOnHandWindow(
                    caster,
                    casterId,
                    container.getAttribute('data-ft-viewer-caster-id') || '',
                ))) return true;
                window.refreshDragAndDrop?.();
                window.fateTideRefreshInventoryUnequippedSearch?.();
                return true;
            case 'bag_window': {
                // Fullscreen bag system: ONE equipped container, rendered standalone in a floating bag
                // window. The container id rides on data-ft-bag-container-id so the surgical caster_update
                // repaint re-reads the right bag.
                const bagId = container.getAttribute('data-ft-bag-container-id') || '';
                const viewerId = container.getAttribute('data-ft-viewer-caster-id') || '';
                const bagHtml = renderBag(caster, bagId, viewerId);
                const bagChanged = paintInventoryMarkup(container, bagHtml);
                if (!bagHtml) {
                    // Container is gone (unequipped / dropped / no longer ours) → close its stale window.
                    // Deferred a frame so we don't mutate the pane list mid-repaint (ftRerenderPcSheet is
                    // iterating panes). The window manager drops it from the open set + un-highlights its tile.
                    requestAnimationFrame(() => window.ftCloseInvSection?.(viewerId, 'bag:' + bagId));
                    return true;
                }
                if (bagChanged) window.refreshDragAndDrop?.();
                return true;
            }
            case 'crafting':
                // On-demand: mounts an inert shell; the recipe catalog (Pattern B) AJAXes in only
                // when the user opens the Crafting tab — never on page load.
                renderCrafting(caster, casterId, container);
                return true;
            case 'prepare_spells':
                // Renders the shell from the blob, then fetches the active class+list table (Pattern B).
                renderPrepareSpells(caster, casterId, container);
                return true;
            case 'summary':
                // The sheet shell: top summary bar, repainted surgically on caster_update.
                renderSummary(caster, casterId, container);
                return true;
            case 'creature_summary': {
                // The UNIFIED CREATURE CARD's summary row (essences/defenses, V·Mana·AC·Luck·AP
                // bars, speeds/senses, combat extras). Mounted as a pane so ftRerenderPcSheet
                // repaints it surgically on caster_update — without this the card's vitality bar
                // went stale after a heal/damage push (the map token + side-info drawer updated,
                // the card didn't, because the row was a plain div, not a pane). The renderer
                // lives in the info-panel bundle, reached via window like renderVManaEcacLuckBarsInline.
                const sideInfo = container.getAttribute('data-ft-pc-side-info') === '1';
                if (!container.id) {
                    container.id = `ft_creature_summary_pane_${casterId.replace(/[^A-Za-z0-9_]/g, '')}`;
                }
                window.renderCreatureSummaryInline?.(casterId, container.id, sideInfo);
                return true;
            }
            case 'creature_status_effects': {
                // The unified creature card's STATUS EFFECTS panel (applied-status icon row + manage
                // button). Mounted as a pane so ftRerenderPcSheet repaints it on caster_update — add or
                // delete a status and the tab updates live off the refreshed cache (without this it only
                // refreshed on a full card re-open). Renderer lives in the info-panel bundle, reached via
                // window like the creature_summary pane.
                if (!container.id) {
                    container.id = `ft_creature_status_effects_pane_${casterId.replace(/[^A-Za-z0-9_]/g, '')}`;
                }
                window.renderCreatureStatusEffectsInline?.(casterId, container.id);
                return true;
            }
            case 'creature_gm': {
                // The unified creature card's GM ACTION STRIP (edit / hide / private / kill-resurrect /
                // destroy / delete / force-refresh / act-as). Mounted as a pane so it hydrates through the
                // global ftMountPcSheet scanner in BOTH the AJAX/PHP card path (loadPostFunctions) AND the
                // cache path — the old plain-div + inline-<script> mount never executed through innerHTML
                // injection (queryData uses innerHTML, which strips <script>), so the GM tab rendered BLANK
                // on the server-built card. A summon (creature ≠ your main PC) falls back to that PHP path,
                // which is why "the GM button shows but the tab is empty" reproduced there. The island
                // self-gates on permission + encounter membership, so a non-GM mount paints empty. Repaints
                // on caster_update like its sibling panes, so the kill↔resurrect button flips live on death.
                if (!container.id) {
                    container.id = `ft_creature_gm_pane_${casterId.replace(/[^A-Za-z0-9_]/g, '')}`;
                }
                window.renderCreatureGmControlsInline?.(casterId, container.id);
                return true;
            }
            case 'creature_header':
                // The unified creature card's HEADER (coords / power level / classification chips).
                // Object-card bundle owns the builder; reached via window like the creature_summary
                // pane, so ftRerenderPcSheet repaints the live coords/PL on caster_update.
                if (!window.ftRenderCreatureHeaderFromCache?.(casterId, container)) container.innerHTML = displayRefusal('unported:creature-display:header-not-held');
                return true;
            case 'creature_core_abilities': {
                // The unified creature card's Core Abilities banner — its use-ability buttons bake live
                // AP/mana/reaction/in_combat gating attrs, plus the stop-bleeding glow and resolve
                // auto-trigger state, and it hosts the special-actions strip. Repaints on caster_update
                // so the banner can't mis-gate ability use mid-combat.
                const sideInfo = container.getAttribute('data-ft-pc-side-info') === '1';
                if (!window.ftRenderCreatureCoreAbilitiesFromCache?.(casterId, container, sideInfo)) container.innerHTML = displayRefusal('unported:creature-display:core-not-held');
                return true;
            }
            default:
                // Unknown pane: fail closed. Every emitted PC/creature pane is TS-owned; the former
                // empty, unreachable legacy PHP bridge was retired once the final pane migrated.
                return false;
        }
    } catch (e) {
        // Keep an unsupported display explicit in its own cell.
        paintMarkup(container, displayRefusal(e));
        return true;
    }
}
